import type { ReactElement } from "react";
import { Info } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  CardBody,
  SettingRow,
  SettingsGroup,
  Switch,
  Tooltip,
} from "@playbookai/ui";
import { useAuthStore } from "../../auth/auth-store.js";
import { getRuntimeConfig } from "../../config/runtime.js";
import {
  playerAiChatRecoveryQueryKey,
  setPlayerAiChatRecovery,
  type PlayerAiChatRecoverySetting,
} from "../../api/tenant-settings-api.js";
import { usePlayerAiChatRecovery } from "../../player-ai/usePlayerAiChatRecovery.js";

/* The tenant's "Backup connection for Player AI" switch (d34), on the GenAI
   configuration page. "Tenant wins" (d33): the value set here overrides the
   deployment's `/config.js` default; while nobody has set it, the row shows
   that default and says so.

   Admin-only via the `/admin` parent route gate; the server is the
   authoritative gate. */

const LABEL = "Backup connection for Player AI";
const HELP =
  "If a company network blocks Player AI's live connection, answers arrive through a slower backup connection instead.";
const EXPLANATION =
  "The browser always tries the live connection first. When it is blocked or silent, the browser asks the server for new messages instead. This workspace's setting overrides the deployment default.";

function effectiveText(
  enabled: boolean | null,
  deploymentDefault: boolean,
): string {
  if (enabled !== null) {
    return enabled ? "On for this workspace." : "Off for this workspace.";
  }
  return deploymentDefault
    ? "On — deployment default."
    : "Off — deployment default.";
}

export function ChatDeliverySettings(): ReactElement {
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? null);
  const queryClient = useQueryClient();
  const setting = usePlayerAiChatRecovery();
  const deploymentDefault = getRuntimeConfig().playerAi.pollFallback;

  const save = useMutation({
    /* The tenant that saved travels with the save, so the answer lands in that
       tenant's cache entry even if the session changed while it was in flight. */
    mutationFn: (variables: { enabled: boolean; tenantId: string | null }) =>
      setPlayerAiChatRecovery(variables.enabled),
    /* A read already in flight carries the value from before this save; if it
       landed after the save it would put the old choice back in the cache,
       the switch and the Player AI client. Cancel it before the write. */
    onMutate: (variables) =>
      queryClient.cancelQueries({
        queryKey: playerAiChatRecoveryQueryKey(variables.tenantId),
      }),
    onSuccess: async (saved, variables) => {
      const queryKey = playerAiChatRecoveryQueryKey(variables.tenantId);
      // A read that started while the save was in flight is no newer either.
      await queryClient.cancelQueries({ queryKey });
      /* The SAME entry the Player AI client's getter reads, so this
         administrator's own chat uses the new value at once. */
      queryClient.setQueryData<PlayerAiChatRecoverySetting>(queryKey, saved);
    },
  });

  const stored = setting.data?.enabled ?? null;
  const checked = setting.data ? (stored ?? deploymentDefault) : false;

  return (
    /* `separated`: the groups above it are inside the Tabs, so the
       first-of-type rule cannot see that this one comes second. */
    <SettingsGroup title="Chat delivery" separated>
      <Card>
        <CardBody className="flex flex-col gap-3">
          <SettingRow
            label={
              <span className="inline-flex items-center gap-1">
                {LABEL}
                <Tooltip content={EXPLANATION} size="lg">
                  {/* xs: the 24px minimum target, beside a text-sm label. */}
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    aria-label="About the backup connection"
                  >
                    <Info className="h-4 w-4" aria-hidden />
                  </Button>
                </Tooltip>
              </span>
            }
            description={
              <>
                {HELP}
                {setting.data ? (
                  <> {effectiveText(stored, deploymentDefault)}</>
                ) : null}
              </>
            }
            control={
              <Switch
                checked={checked}
                onChange={(enabled) => save.mutate({ enabled, tenantId })}
                disabled={!setting.data || save.isPending}
                aria-label={LABEL}
              />
            }
          />
          {setting.isError && !setting.data ? (
            <div className="flex flex-col items-start gap-3">
              <Alert
                tone="critical"
                title="We couldn't load the backup connection setting"
              >
                {setting.error.message}
              </Alert>
              <Button
                variant="secondary"
                onClick={() => void setting.refetch()}
                disabled={setting.isFetching}
              >
                {setting.isFetching ? "Retrying..." : "Retry"}
              </Button>
            </div>
          ) : null}
          {save.isError ? (
            <Alert tone="critical" title="Couldn't save this setting">
              {save.error.message}
            </Alert>
          ) : save.isSuccess ? (
            <Alert tone="healthy">
              {save.data.enabled
                ? "Backup connection turned on."
                : "Backup connection turned off."}
            </Alert>
          ) : null}
        </CardBody>
      </Card>
    </SettingsGroup>
  );
}
